"use client";

import { useEffect, useMemo, useState } from "react";
import { useLenis } from "lenis/react";
import { FlipCard, type FlipCardProps } from "@/components/ui/FlipCard";
import {
  InfiniteDraggableSlider,
  type InfiniteDraggableSlide,
} from "@/components/ui/InfiniteDraggableSlider";
import { DragExploreCursor } from "@/components/ui/DragExploreCursor";
import {
  openImageLightbox,
  preloadImageLightbox,
} from "@/components/ui/ImageLightbox";
import { VideoModal } from "@/components/ui/VideoModal";
import { Reveal, RevealHeader } from "@/components/ui/Reveal";

export type PublicationItem = Omit<FlipCardProps, "className"> & {
  videoUrl?: string;
};

export type PublicationCategory = {
  id: string;
  label: string;
  items: PublicationItem[];
};

const defaultCategories: PublicationCategory[] = [
  {
    id: "published-books",
    label: "Published Books",
    items: [
      {
        imageSrc:
          "/landing/Real%20Results/published-book/the-legend-of-denali.jpg",
        imageAlt: "The Legend of Denali book cover",
        title: "The Legend Of Denali",
        author: "Gregory English",
      },
      {
        imageSrc: "/landing/Real%20Results/published-book/allen-court-new.jpg",
        imageAlt: "The Life and Times of Allen Court book cover",
        title: "The Life And Times Of Allen Court",
        author: "Meshawn Deberry",
      },
      {
        imageSrc: "/landing/Real%20Results/published-book/new-life-leaving.jpg",
        imageAlt: "My New Life Leaving book cover",
        title: "My New Life Leaving",
        author: "Dr.Vera M.Stephens-Stevens",
      },
      {
        imageSrc: "/landing/Real%20Results/published-book/the-hug.jpg",
        imageAlt: "The Hug book cover",
        title: "The Hug",
        author: "Britt j.Palmer",
      },
      {
        imageSrc: "/landing/Real%20Results/published-book/barnaby-bunny.jpg",
        imageAlt: "Barnaby the One-Eared Bunny book cover",
        title: "Barnaby The One-Eared...",
        author: "Colleen Severin",
      },
    ],
  },
  {
    id: "author-websites",
    label: "Author Websites",
    items: [
      {
        imageSrc: "/landing/Real%20Results/author-website/author-web1.webp",
        imageAlt: "Author website example one",
        title: "Author Website One",
      },
      {
        imageSrc: "/landing/Real%20Results/author-website/author-web2.webp",
        imageAlt: "Author website example two",
        title: "Author Website Two",
      },
      {
        imageSrc: "/landing/Real%20Results/author-website/author-web3.webp",
        imageAlt: "Author website example three",
        title: "Author Website Three",
      },
      {
        imageSrc: "/landing/Real%20Results/author-website/author-web4.webp",
        imageAlt: "Author website example four",
        title: "Author Website Four",
      },
      {
        imageSrc: "/landing/Real%20Results/author-website/author-web6.webp",
        imageAlt: "Author website example five",
        title: "Author Website Five",
      },
    ],
  },
  {
    id: "book-illustrations",
    label: "Book Illustrations",
    items: [
      {
        imageSrc: "/landing/Real%20Results/book-illustration/1.png",
        imageAlt: "Children's book illustration",
        title: "Children's Illustrations",
      },
      {
        imageSrc: "/landing/Real%20Results/book-illustration/2.png",
        imageAlt: "Cover illustration sample",
        title: "Cover Illustrations",
      },
      {
        imageSrc: "/landing/Real%20Results/book-illustration/3.png",
        imageAlt: "Interior illustration sample",
        title: "Interior Spot Art",
      },
      {
        imageSrc: "/landing/Real%20Results/book-illustration/4.webp",
        imageAlt: "Character design sample",
        title: "Character Design",
      },
      {
        imageSrc: "/landing/Real%20Results/book-illustration/5.webp",
        imageAlt: "Full-page illustration sample",
        title: "Full-Page Spreads",
      },
      {
        imageSrc: "/landing/Real%20Results/book-illustration/6.webp",
        imageAlt: "Book illustration sample six",
        title: "Illustration Six",
      },
    ],
  },
  {
    id: "video-trailers",
    label: "Video Trailers",
    items: [
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/alexander-deutsch-book-trailer.webp",
        imageAlt: "Alexander Deutsch book trailer",
        title: "Alexander Deutsch",
        videoUrl: "https://youtu.be/k058aBU3H9I?si=wtMym0SrGEzkGK2p",
      },
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/barbara-book-trailer.webp",
        imageAlt: "Barbara book trailer",
        title: "Barbara",
        videoUrl: "https://youtu.be/xxohDBNlUhs?si=fpxJ5zN-8MGM1sAJ",
      },
      {
        imageSrc: "/landing/Real%20Results/video-trailer/debora-johnson.webp",
        imageAlt: "Debora Johnson book trailer",
        title: "Debora Johnson",
        videoUrl: "https://youtu.be/5tCf9RJNQdU?si=ciY453qFMpy575C0",
      },
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/idres-hafiz-book-trailer.webp",
        imageAlt: "Idres Hafiz book trailer",
        title: "Idres Hafiz",
        videoUrl: "https://youtu.be/OvSs3h_orzk?si=Z8JU2CPU_IroQz_F",
      },
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/in-the-search-of-north-star.webp",
        imageAlt: "In The Search Of North Star book trailer",
        title: "In The Search Of North Star",
        videoUrl: "https://youtu.be/zP7Hfivpig4?si=G9JXmGGMCA3xcGJD",
      },
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/jerry-sofer-book-trailer.webp",
        imageAlt: "Jerry Sofer book trailer",
        title: "Jerry Sofer",
        videoUrl: "https://youtu.be/Kqmq6dj7rP4?si=0z-1C2hL_htGr9Uh",
      },
      {
        imageSrc: "/landing/Real%20Results/video-trailer/makhaila-thorne.webp",
        imageAlt: "Makhaila Thorne book trailer",
        title: "Makhaila Thorne",
        videoUrl: "https://youtu.be/8VJ9AsXGeck?si=HxsENHCCwlTfFxTu",
      },
      {
        imageSrc:
          "/landing/Real%20Results/video-trailer/miracle-in-the-mess-mike-farias.webp",
        imageAlt: "Miracle In The Mess Mike Farias book trailer",
        title: "Miracle In The Mess",
        videoUrl: "https://youtu.be/XdXfpCdym6s?si=7amXXVEYYD9K18iZ",
      },
    ],
  },
  {
    id: "book-marketing",
    label: "Book Marketing",
    items: [
      {
        imageSrc: "/landing/Real%20Results/book-marketing/1.webp",
        imageAlt: "Book marketing result one",
        title: "Book Marketing One",
      },
      {
        imageSrc: "/landing/Real%20Results/book-marketing/2.webp",
        imageAlt: "Book marketing result two",
        title: "Book Marketing Two",
      },
      {
        imageSrc: "/landing/Real%20Results/book-marketing/3.webp",
        imageAlt: "Book marketing result three",
        title: "Book Marketing Three",
      },
      {
        imageSrc: "/landing/Real%20Results/book-marketing/4.webp",
        imageAlt: "Book marketing result four",
        title: "Book Marketing Four",
      },
      {
        imageSrc: "/landing/Real%20Results/book-marketing/5.webp",
        imageAlt: "Book marketing result five",
        title: "Book Marketing Five",
      },
      {
        imageSrc: "/landing/Real%20Results/book-marketing/6.webp",
        imageAlt: "Book marketing result six",
        title: "Book Marketing Six",
      },
    ],
  },
];

export type SuccessfulPublicationsProps = {
  eyebrow?: string;
  heading?: string;
  categories?: PublicationCategory[];
  defaultActiveId?: string;
  className?: string;
};

export function SuccessfulPublications({
  eyebrow = "Building Trust Via Real Results",
  heading = "Some Of Our Successful Publication",
  categories = defaultCategories,
  defaultActiveId,
  className,
}: SuccessfulPublicationsProps) {
  const [activeId, setActiveId] = useState(
    defaultActiveId ?? categories[0]?.id ?? "",
  );
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const lenis = useLenis();
  const activeCategory =
    categories.find((category) => category.id === activeId) ?? categories[0];

  const authorWebsiteItems = useMemo(
    () =>
      categories.find((category) => category.id === "author-websites")?.items ??
      [],
    [categories],
  );

  useEffect(() => {
    if (activeId === "author-websites") {
      preloadImageLightbox();
    }
  }, [activeId]);

  const authorWebsiteSlides: InfiniteDraggableSlide[] =
    activeCategory?.id === "author-websites"
      ? activeCategory.items.map((item) => ({
          imageSrc: item.imageSrc,
          imageAlt: item.imageAlt,
          title: item.title,
        }))
      : [];

  const bookIllustrationSlides: InfiniteDraggableSlide[] =
    activeCategory?.id === "book-illustrations"
      ? activeCategory.items.map((item) => ({
          imageSrc: item.imageSrc,
          imageAlt: item.imageAlt,
          title: item.title,
        }))
      : [];

  const bookMarketingSlides: InfiniteDraggableSlide[] =
    activeCategory?.id === "book-marketing"
      ? activeCategory.items.map((item) => ({
          imageSrc: item.imageSrc,
          imageAlt: item.imageAlt,
          title: item.title,
        }))
      : [];

  const activeVideoItems = useMemo(
    () => categories.find((category) => category.id === "video-trailers")?.items ?? [],
    [categories],
  );

  const isSliderTab =
    activeCategory?.id === "author-websites" ||
    activeCategory?.id === "book-illustrations" ||
    activeCategory?.id === "book-marketing" ||
    activeCategory?.id === "video-trailers";

  const handleAuthorWebsiteClick = (index: number) => {
    void openImageLightbox(
      authorWebsiteItems.map((item) => ({
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt,
        title: item.title,
      })),
      index,
      {
        onOpen: () => {
          document.body.style.overflow = "hidden";
          lenis?.stop();
        },
        onClose: () => {
          document.body.style.overflow = "";
          lenis?.start();
        },
      },
    );
  };

  const sliderContent =
    activeCategory?.id === "author-websites" ? (
      <InfiniteDraggableSlider
        slides={authorWebsiteSlides}
        interaction="zoom"
        onSlideClick={handleAuthorWebsiteClick}
      />
    ) : activeCategory?.id === "book-illustrations" ? (
      <InfiniteDraggableSlider
        slides={bookIllustrationSlides}
        cardVariant="portrait"
      />
    ) : activeCategory?.id === "book-marketing" ? (
      <InfiniteDraggableSlider
        slides={bookMarketingSlides}
        cardVariant="square"
      />
    ) : activeCategory?.id === "video-trailers" ? (
      <InfiniteDraggableSlider
        slides={activeCategory.items.map((item) => ({
          imageSrc: item.imageSrc,
          imageAlt: item.imageAlt,
          title: item.title,
        }))}
        onSlideClick={(index) => setActiveVideoIndex(index)}
      />
    ) : null;

  return (
    <section
      data-feather-section="publications"
      className={["overflow-x-clip bg-[#2d3e26] py-16 lg:py-24", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RevealHeader
          align="center"
          eyebrow={
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.2em] text-[#f1d0ae] sm:text-sm">
              {eyebrow}
            </p>
          }
          heading={
            <h2 className="mt-3 font-heading text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
              {heading}
            </h2>
          }
        />

        <Reveal
          variant="up"
          delayMs={200}
          className="mt-8 flex flex-wrap items-center justify-center gap-y-2"
          role="tablist"
          aria-label="Publication categories"
        >
          {categories.map((category, index) => {
            const isActive = category.id === activeCategory?.id;

            return (
              <div key={category.id} className="flex items-center">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveId(category.id)}
                  className={[
                    "px-3 py-1 font-heading text-sm font-semibold transition-colors sm:text-base",
                    isActive
                      ? "text-[#f1d0ae]"
                      : "text-white hover:text-[#f1d0ae]",
                  ].join(" ")}
                >
                  {category.label}
                </button>
                {index < categories.length - 1 && (
                  <span className="mx-1 text-white/50" aria-hidden="true">
                    |
                  </span>
                )}
              </div>
            );
          })}
        </Reveal>

        <Reveal variant="zoom" delayMs={280}>
        <div role="tabpanel" aria-label={activeCategory?.label} className="mt-10">
          {isSliderTab && sliderContent ? (
            <DragExploreCursor>{sliderContent}</DragExploreCursor>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-6">
              {activeCategory?.items.map((item) => (
                <FlipCard key={`${activeCategory.id}-${item.title}`} {...item} />
              ))}
            </div>
          )}
        </div>
        </Reveal>
      </div>
      {activeVideoIndex !== null ? (
        <VideoModal
          open={activeVideoIndex !== null}
          onClose={() => setActiveVideoIndex(null)}
          title={activeVideoItems[activeVideoIndex]?.title ?? "Video trailer"}
          videoUrl={activeVideoItems[activeVideoIndex]?.videoUrl}
          galleryItems={activeVideoItems.map((item, index) => ({
            imageSrc: item.imageSrc,
            imageAlt: item.imageAlt,
            title: item.title,
            videoUrl: item.videoUrl,
            isActive: index === activeVideoIndex,
          }))}
          onSelectGalleryItem={(index) => setActiveVideoIndex(index)}
          onPrevious={() =>
            setActiveVideoIndex(
              (current) =>
                (current === null ? 0 : (current - 1 + activeVideoItems.length) % activeVideoItems.length),
            )
          }
          onNext={() =>
            setActiveVideoIndex(
              (current) => (current === null ? 0 : (current + 1) % activeVideoItems.length),
            )
          }
        />
      ) : null}
    </section>
  );
}
